import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Copy, X, Check, MessageSquarePlus, Edit3 } from 'lucide-react';
import { usePendingCADetail } from './usePendingCa';
import type { PendingCADetail } from './pendingCa.types';
import { DisclosureBodyView } from '../../components/DisclosureBodyView';
import '../../components/disclosureBodyView.css';

// 권위: ARCH_DASHBOARD §4.10 상세 모달 — 섹션 A~E.
//
// 섹션 A: 공시 원본 (DART link)
// 섹션 B: LLM 추출 결과 — 편집 가능 폼 + read-only JSON
// 섹션 C: 규칙 재검증 — rule_verified_factor vs llm 값 비교
// 섹션 D: 액션 — 승인 / 반려(사유) / 수정 후 승인 / 사후 주석
// 섹션 E: 감사 이력 — review_status / reviewer / reviewed_at / review_note

export interface PendingCADetailModalProps {
  auditId: number | null;
  onClose: () => void;
  onApprove: (auditId: number) => Promise<void> | void;
  onReject: (auditId: number, note: string) => Promise<void> | void;
  onEditApprove: (
    auditId: number,
    extractedEvent: Record<string, unknown>,
    note?: string
  ) => Promise<void> | void;
  onAnnotate: (auditId: number, note: string) => Promise<void> | void;
  busy?: boolean;
}

interface FormState {
  event_type: string;
  ex_date: string;
  ratio_old: string;
  ratio_new: string;
  dividend_ratio: string;
  adjustment_factor: string;
  rationale: string;
}

function toFormState(d: PendingCADetail | undefined): FormState {
  const e = (d?.extractedEvent ?? {}) as Record<string, unknown>;
  return {
    event_type: str(e.event_type),
    ex_date: str(e.ex_date),
    ratio_old: str(e.ratio_old),
    ratio_new: str(e.ratio_new),
    dividend_ratio: str(e.dividend_ratio),
    adjustment_factor: str(e.adjustment_factor),
    rationale: str(e.rationale),
  };
}

function str(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function formToExtracted(f: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.event_type) out.event_type = f.event_type;
  if (f.ex_date) out.ex_date = f.ex_date;
  if (f.ratio_old) out.ratio_old = Number(f.ratio_old);
  if (f.ratio_new) out.ratio_new = Number(f.ratio_new);
  if (f.dividend_ratio) out.dividend_ratio = Number(f.dividend_ratio);
  if (f.adjustment_factor) out.adjustment_factor = Number(f.adjustment_factor);
  if (f.rationale) out.rationale = f.rationale;
  return out;
}

function validate(f: FormState): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!f.event_type) errs.event_type = 'event_type 필수';
  if (!f.ex_date) errs.ex_date = 'ex_date 필수';
  // 분할/병합/무상증자 계열은 비율 필수
  const needsRatio = ['split', 'reverse_split', 'bonus_issue', 'stock_dividend'].includes(
    f.event_type
  );
  if (needsRatio) {
    if (!f.ratio_old) errs.ratio_old = 'ratio_old 필수';
    if (!f.ratio_new) errs.ratio_new = 'ratio_new 필수';
    if (f.ratio_old && Number.isNaN(Number(f.ratio_old))) errs.ratio_old = '숫자만';
    if (f.ratio_new && Number.isNaN(Number(f.ratio_new))) errs.ratio_new = '숫자만';
  }
  if (f.adjustment_factor && Number.isNaN(Number(f.adjustment_factor)))
    errs.adjustment_factor = '숫자만';
  if (f.dividend_ratio && Number.isNaN(Number(f.dividend_ratio)))
    errs.dividend_ratio = '숫자만';
  return errs;
}

export function PendingCADetailModal({
  auditId,
  onClose,
  onApprove,
  onReject,
  onEditApprove,
  onAnnotate,
  busy,
}: PendingCADetailModalProps): JSX.Element | null {
  const detail = usePendingCADetail(auditId);
  const data = detail.data;

  const [form, setForm] = useState<FormState>(toFormState(data));
  const [rejectNote, setRejectNote] = useState('');
  const [annotation, setAnnotation] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // detail 새로 로드되면 폼 동기화 (auditId 가 바뀐 경우)
  useEffect(() => {
    setForm(toFormState(data));
    setShowRejectInput(false);
    setRejectNote('');
    setAnnotation('');
  }, [auditId, data]);

  const errors = useMemo(() => validate(form), [form]);
  const editedFields = useMemo(() => diffFields(toFormState(data), form), [data, form]);

  if (auditId == null) return null;

  const isTerminal =
    data?.reviewStatus === 'approved' || data?.reviewStatus === 'rejected';
  const ruleMismatch =
    data?.ruleVerifiedFactor != null &&
    data?.extractedEvent?.adjustment_factor != null &&
    Math.abs(
      Number(data.extractedEvent.adjustment_factor) - Number(data.ruleVerifiedFactor)
    ) > 1e-6;

  return (
    <div
      className="pca-modal__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pca-modal" role="dialog" aria-label="Pending CA 상세">
        <div className="pca-modal__header">
          <h3 className="pca-modal__title">
            audit_id {auditId}{' '}
            {data && (
              <span
                className={`pca-status-badge pca-status-badge--${data.reviewStatus}`}
              >
                {data.reviewStatus}
              </span>
            )}
          </h3>
          <button
            type="button"
            className="pca-modal__close"
            aria-label="close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>

        <div className="pca-modal__body">
          {detail.isLoading && <div>불러오는 중…</div>}
          {detail.isError && (
            <div className="pca-warning-banner">
              상세 로드 실패: {(detail.error as Error).message}
            </div>
          )}

          {data && (
            <>
              {/* 섹션 A — 공시 원본 */}
              <section className="pca-section">
                <h4 className="pca-section__title">A. 공시 원본</h4>
                <div>
                  <strong>{data.disclosureTitle ?? '(제목 없음)'}</strong>
                </div>
                <div className="pca-muted">
                  rcpt_no {data.rcptNo ?? '-'} · corp_code {data.corpCode ?? '-'} ·
                  symbol {data.symbol ?? '-'} · created {formatKst(data.createdAt)}
                </div>
                {data.disclosureUrl && (
                  <a
                    href={data.disclosureUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 13 }}
                  >
                    DART 원본 열기 <ExternalLink size={14} />
                  </a>
                )}
                {data.disclosureBody && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12 }}>
                      공시 본문 펼치기 ({data.disclosureBody.length.toLocaleString('ko-KR')} 자)
                    </summary>
                    <div style={{ marginTop: 8 }}>
                      <DisclosureBodyView body={data.disclosureBody} />
                    </div>
                  </details>
                )}
              </section>

              {/* 섹션 B — LLM 추출 결과 (편집 가능) */}
              <section className="pca-section">
                <h4 className="pca-section__title">B. LLM 추출 결과 (편집)</h4>
                <div className="pca-form-grid">
                  <Field label="event_type" error={errors.event_type}>
                    <select
                      className="pca-select"
                      value={form.event_type}
                      onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                      disabled={isTerminal}
                    >
                      <option value="">-</option>
                      {[
                        'split',
                        'reverse_split',
                        'bonus_issue',
                        'stock_dividend',
                        'rights_offering',
                        'cash_dividend',
                      ].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="ex_date" error={errors.ex_date}>
                    <input
                      type="date"
                      className="pca-input"
                      value={form.ex_date}
                      onChange={(e) => setForm({ ...form, ex_date: e.target.value })}
                      disabled={isTerminal}
                    />
                  </Field>
                  <Field label="ratio_old" error={errors.ratio_old}>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="pca-input"
                      value={form.ratio_old}
                      onChange={(e) => setForm({ ...form, ratio_old: e.target.value })}
                      disabled={isTerminal}
                    />
                  </Field>
                  <Field label="ratio_new" error={errors.ratio_new}>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="pca-input"
                      value={form.ratio_new}
                      onChange={(e) => setForm({ ...form, ratio_new: e.target.value })}
                      disabled={isTerminal}
                    />
                  </Field>
                  <Field label="dividend_ratio" error={errors.dividend_ratio}>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="pca-input"
                      value={form.dividend_ratio}
                      onChange={(e) =>
                        setForm({ ...form, dividend_ratio: e.target.value })
                      }
                      disabled={isTerminal}
                    />
                  </Field>
                  <Field
                    label="adjustment_factor"
                    error={errors.adjustment_factor}
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      className="pca-input"
                      value={form.adjustment_factor}
                      onChange={(e) =>
                        setForm({ ...form, adjustment_factor: e.target.value })
                      }
                      disabled={isTerminal}
                    />
                  </Field>
                </div>
                <Field label="rationale (LLM 설명)">
                  <textarea
                    className="pca-textarea"
                    value={form.rationale}
                    onChange={(e) => setForm({ ...form, rationale: e.target.value })}
                    disabled={isTerminal}
                  />
                </Field>

                <div className="pca-muted">
                  llm_model {data.llmModel ?? '-'} · cost ${fmt(data.llmCostUsd, 4)} ·
                  prompt_hash{' '}
                  <code>{shortHash(data.llmPromptHash)}</code>{' '}
                  {data.llmPromptHash && (
                    <button
                      type="button"
                      className="pca-btn pca-btn--ghost"
                      style={{ height: 22, padding: '0 6px' }}
                      onClick={() =>
                        navigator.clipboard?.writeText(data.llmPromptHash ?? '')
                      }
                      title="prompt_hash 복사"
                    >
                      <Copy size={11} />
                    </button>
                  )}
                </div>

                {data.llmResponseRaw && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12 }}>
                      LLM 원본 응답 (read-only)
                    </summary>
                    <pre className="pca-readonly-json">
                      {JSON.stringify(data.llmResponseRaw, null, 2)}
                    </pre>
                  </details>
                )}

                {editedFields.length > 0 && !isTerminal && (
                  <div className="pca-muted">
                    수정된 필드: <strong>{editedFields.join(', ')}</strong>
                  </div>
                )}
              </section>

              {/* 섹션 C — 규칙 재검증 */}
              <section className="pca-section">
                <h4 className="pca-section__title">C. 규칙 재검증</h4>
                <div>
                  rule_verified_factor:{' '}
                  <strong>{fmt(data.ruleVerifiedFactor, 6)}</strong> · llm
                  adjustment_factor:{' '}
                  <strong>{fmt(toNum(data.extractedEvent?.adjustment_factor), 6)}</strong>
                </div>
                {ruleMismatch && (
                  <div className="pca-warning-banner">
                    LLM 값과 규칙 재계산값이 일치하지 않습니다. 수식값({fmt(data.ruleVerifiedFactor, 6)}) 채택을 권장합니다.
                  </div>
                )}
              </section>

              {/* 섹션 D — 액션 영역은 footer 로 이동 */}

              {/* 섹션 E — 감사 이력 */}
              <section className="pca-section">
                <h4 className="pca-section__title">E. 감사 이력</h4>
                <table className="pca-table" data-testid="pca-audit-history">
                  <thead>
                    <tr>
                      <th>review_status</th>
                      <th>reviewer</th>
                      <th>reviewed_at</th>
                      <th>resync_status</th>
                      <th>review_note</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{data.reviewStatus}</td>
                      <td>{data.reviewer ?? '-'}</td>
                      <td>{data.reviewedAt ? formatKst(data.reviewedAt) : '-'}</td>
                      <td>{data.resyncStatus ?? '-'}</td>
                      <td style={{ whiteSpace: 'pre-wrap' }}>{data.reviewNote ?? '-'}</td>
                    </tr>
                  </tbody>
                </table>
                {data.resyncError && (
                  <div className="pca-warning-banner">resync_error: {data.resyncError}</div>
                )}
                {data.corporateAction && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12 }}>
                      corporate_actions row #{data.corporateAction.id}
                    </summary>
                    <pre className="pca-readonly-json">
                      {JSON.stringify(data.corporateAction, null, 2)}
                    </pre>
                  </details>
                )}
              </section>

              {/* 사후 주석 */}
              <section className="pca-section">
                <h4 className="pca-section__title">사후 주석 추가</h4>
                <textarea
                  className="pca-textarea"
                  placeholder="감사 메모를 남기면 review_note 에 누적됩니다."
                  value={annotation}
                  onChange={(e) => setAnnotation(e.target.value)}
                />
                <div>
                  <button
                    type="button"
                    className="pca-btn"
                    disabled={!annotation.trim() || busy}
                    onClick={() => {
                      onAnnotate(auditId, annotation.trim());
                      setAnnotation('');
                    }}
                  >
                    <MessageSquarePlus size={14} strokeWidth={1.75} />
                    주석 추가
                  </button>
                </div>
              </section>

              {/* 반려 사유 입력 */}
              {showRejectInput && (
                <section className="pca-section">
                  <h4 className="pca-section__title">반려 사유</h4>
                  <textarea
                    className="pca-textarea"
                    placeholder="반려 사유를 입력하세요."
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                  />
                </section>
              )}
            </>
          )}
        </div>

        {data && !isTerminal && (
          <div className="pca-modal__actions">
            {!showRejectInput ? (
              <>
                <button
                  type="button"
                  className="pca-btn pca-btn--danger"
                  disabled={busy}
                  onClick={() => setShowRejectInput(true)}
                >
                  <X size={14} strokeWidth={1.75} />
                  반려
                </button>
                <button
                  type="button"
                  className="pca-btn"
                  disabled={busy || editedFields.length === 0 || Object.keys(errors).length > 0}
                  onClick={() => onEditApprove(auditId, formToExtracted(form))}
                  title={
                    editedFields.length === 0
                      ? '수정 사항이 없습니다.'
                      : Object.keys(errors).length > 0
                        ? '필수 필드 검증 실패'
                        : '수정값으로 승인합니다.'
                  }
                >
                  <Edit3 size={14} strokeWidth={1.75} />
                  수정 후 승인
                </button>
                <button
                  type="button"
                  className="pca-btn pca-btn--primary"
                  disabled={busy}
                  onClick={() => onApprove(auditId)}
                >
                  <Check size={14} strokeWidth={2} />
                  승인
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="pca-btn pca-btn--ghost"
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectNote('');
                  }}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="pca-btn pca-btn--danger"
                  disabled={busy || !rejectNote.trim()}
                  onClick={() => onReject(auditId, rejectNote.trim())}
                >
                  <X size={14} strokeWidth={1.75} />
                  반려 확정
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="pca-field">
      <label className="pca-field__label">{label}</label>
      {children}
      {error && <span className="pca-field__error">{error}</span>}
    </div>
  );
}

function diffFields(a: FormState, b: FormState): string[] {
  const out: string[] = [];
  for (const k of Object.keys(a) as Array<keyof FormState>) {
    if (a[k] !== b[k]) out.push(k);
  }
  return out;
}

function fmt(n: number | null | undefined, digits: number): string {
  if (n == null) return '-';
  const num = typeof n === 'number' ? n : Number(n);
  if (Number.isNaN(num)) return '-';
  return num.toFixed(digits);
}

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function shortHash(h: string | null): string {
  if (!h) return '-';
  return h.length <= 12 ? h : h.slice(0, 8) + '…' + h.slice(-4);
}

function formatKst(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', { hour12: false });
  } catch {
    return iso;
  }
}
